// ====================================
// DATABASE TYPES
// ====================================

export type SourceType = 'question' | 'calculation' | 'client' | 'static';
export type CalculationType = 'averages' | 'role-stats' | 'comparative' | 'percentages';
export type QuestionGroup = 'mentor' | 'manager' | 'equipo';

// Client field types (campos de la tabla clientes)
export type ClientFieldType =
  | 'nombre'
  | 'logo'
  | 'mision'
  | 'created_at';

// Static field types (valores auto-generados)
export type StaticType = 'current_date';
export type DateFormatType = 'MMMM YYYY' | 'MM/YYYY' | 'DD/MM/YYYY' | 'YYYY-MM-DD';

// ====================================
// CALCULATION CONFIGS
// ====================================

export interface AveragesConfig {
  operation: 'averages';
  category?: string; // Si no se especifica, promedio general
  group: QuestionGroup;
  options?: {
    decimals?: number;
    excludeZeros?: boolean;
  };
}

export interface RoleStatsConfig {
  operation: 'role-stats';
  role?: string; // Si no se especifica, promedio de todos los roles
  category?: string; // Si no se especifica, promedio general del rol
  group: QuestionGroup;
}

export interface ComparativeConfig {
  operation: 'comparative';
  individualGroup: QuestionGroup; // e.g., 'mentor'
  referenceGroup: QuestionGroup; // e.g., 'equipo'
  category?: string; // Si no se especifica, compara promedios generales
  threshold?: number; // Para clasificar fortalezas/debilidades
}

export interface PercentilesConfig {
  operation: 'percentages';
  category?: string;
  group: QuestionGroup;
  percentile?: 25 | 50 | 75 | 90 | 95; // Cual percentil retornar
}

export interface ClientConfig {
  // Campo de la tabla clientes a renderizar
  // El cliente se resuelve automáticamente: submission → launch → client
  client_field: ClientFieldType;
}

export interface StaticConfig {
  // Tipo de valor estático a generar
  static_type: StaticType;
  // Formato para fechas (solo aplica cuando static_type es 'current_date')
  format?: DateFormatType;
}

export type CalculationConfig =
  | AveragesConfig
  | RoleStatsConfig
  | ComparativeConfig
  | PercentilesConfig
  | ClientConfig
  | StaticConfig;

// ====================================
// VARIABLE MAPPING
// ====================================

export interface VariableMapping {
  id: number;
  project_id: number;
  template_id: number;

  // Variable info
  variable_name: string;
  variable_placeholder: string;

  // Source type
  source_type: SourceType;

  // Question source
  question_id?: number;
  question_group?: QuestionGroup;
  question_name?: string;
  form_source?: 'main' | 'referral'; // Which form this question comes from

  // Matrix fields (for control_matrix questions)
  matrix_row_id?: string;
  matrix_row_text?: string;
  matrix_column_id?: string;
  matrix_column_text?: string;

  // Calculation source
  calculation_type?: CalculationType;
  calculation_config?: CalculationConfig;

  // Client source - Se almacena en calculation_config cuando source_type es 'client'
  // calculation_config: { client_field: ClientFieldType }
  // NOTA: El cliente se resuelve automáticamente: submission → launch → client

  // Metadata
  description?: string;
  default_value?: string;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Joined data
  question?: {
    id: number;
    name: string;
    text: string;
  };
}

// ====================================
// FORM TYPES (para UI)
// ====================================

export interface VariableMappingFormData {
  variable_name: string;
  variable_placeholder: string;
  source_type: SourceType;

  // Question fields
  question_id?: number;
  question_group?: QuestionGroup;
  question_name?: string;
  form_source?: 'main' | 'referral'; // Which form this question comes from

  // Matrix fields (for control_matrix questions)
  matrix_row_id?: string;
  matrix_row_text?: string;
  matrix_column_id?: string;
  matrix_column_text?: string;

  // Calculation fields
  calculation_type?: CalculationType;
  calculation_config?: CalculationConfig;

  // Client fields - Se almacena en calculation_config cuando source_type es 'client'
  // calculation_config: { client_field: ClientFieldType }
  // NOTA: El cliente se resuelve automáticamente: submission → launch → client

  // Metadata
  description?: string;
  default_value?: string;
}

// ====================================
// CALCULATION OPTIONS (para selects)
// ====================================

export interface CalculationOption {
  value: CalculationType;
  label: string;
  description: string;
  requiresCategory: boolean;
  requiresRole: boolean;
  requiresGroups: 'single' | 'multiple';
}

export const CALCULATION_OPTIONS: CalculationOption[] = [
  {
    value: 'averages',
    label: 'Promedio',
    description: 'Calcula el promedio de una categoría o general',
    requiresCategory: false,
    requiresRole: false,
    requiresGroups: 'single',
  },
  {
    value: 'role-stats',
    label: 'Estadísticas por Rol',
    description: 'Calcula promedio de un rol específico',
    requiresCategory: false,
    requiresRole: true,
    requiresGroups: 'single',
  },
  {
    value: 'comparative',
    label: 'Comparativa',
    description: 'Compara dos grupos (ej: mentor vs equipo)',
    requiresCategory: false,
    requiresRole: false,
    requiresGroups: 'multiple',
  },
  {
    value: 'percentages',
    label: 'Percentiles',
    description: 'Calcula percentiles de una categoría',
    requiresCategory: false,
    requiresRole: false,
    requiresGroups: 'single',
  },
];

// ====================================
// CLIENT FIELD OPTIONS
// ====================================

export interface ClientFieldOption {
  value: ClientFieldType;
  label: string;
  description: string;
}

export const CLIENT_FIELD_OPTIONS: ClientFieldOption[] = [
  {
    value: 'nombre',
    label: 'Nombre del Cliente',
    description: 'Nombre de la organización cliente',
  },
  {
    value: 'logo',
    label: 'Logo del Cliente',
    description: 'URL del logo de la organización cliente',
  },
  {
    value: 'mision',
    label: 'Misión del Cliente',
    description: 'Misión o propósito de la organización cliente',
  },
  {
    value: 'created_at',
    label: 'Fecha de Creación del Cliente',
    description: 'Fecha en que se registró el cliente en el sistema',
  },
];

// ====================================
// STATIC FIELD OPTIONS
// ====================================

export interface StaticFieldOption {
  value: StaticType;
  label: string;
  description: string;
}

export const STATIC_TYPE_OPTIONS: StaticFieldOption[] = [
  {
    value: 'current_date',
    label: 'Fecha Actual',
    description: 'Fecha en la que se genera el informe',
  },
];

export interface DateFormatOption {
  value: DateFormatType;
  label: string;
  example: string;
}

export const DATE_FORMAT_OPTIONS: DateFormatOption[] = [
  {
    value: 'MMMM YYYY',
    label: 'Mes completo y año',
    example: 'Noviembre 2025',
  },
  {
    value: 'MM/YYYY',
    label: 'Mes numérico y año',
    example: '11/2025',
  },
  {
    value: 'DD/MM/YYYY',
    label: 'Día, mes y año',
    example: '25/11/2025',
  },
  {
    value: 'YYYY-MM-DD',
    label: 'Formato ISO',
    example: '2025-11-25',
  },
];

// ====================================
// EXTRACTED VARIABLE (from PPT)
// ====================================

export interface ExtractedVariable {
  name: string; // e.g., "nombre_mentor"
  placeholder: string; // e.g., "{nombre_mentor}"
  occurrences: number; // Cuántas veces aparece en el PPT
  slides: number[]; // En qué slides aparece
  mapped: boolean; // Si ya tiene mapping
}
